import {Nullable} from '../../app/TypeHelpers'
import {
    BusinessSchedule,
    DurationFacilitySetting,
    OptionSetting,
    OvernightFacilitySetting,
} from '../facilities/FacilityForReservation'
import {PaymentInformation, ReservationOption, ReserveFacilityModelPaymentObserver} from './ReserveFacilityModel'
import {CHARGE_UNIT, UNIT_OF_BUSINESS_SCHEDULE, UNIT_TIME, VALID_OR_NOT} from '../../CodeMaster'
import moment from 'moment'
import ReservePaymentCalculator from './paymentCalculators/ReservePaymentCalculator'
import ReservePaymentCalculatorFactory from './paymentCalculators/ReservePaymentCalculatorFactory'
import {convertToNullableMoment} from './helpers/helpers'
import PaymentModelSchedulePreProcessor from './PaymentModelSchedulePreProcessor'

export interface BusinessScheduleWithDateTime {
    businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE
    dateStraddle: VALID_OR_NOT
    joinPrevDaysSchedule: VALID_OR_NOT
    startDateMoment: moment.Moment
    endDateMoment: moment.Moment
    charge: Nullable<number>
    chargeUnit: Nullable<CHARGE_UNIT>
    chargeUnitTime: Nullable<number>
    changedChargeApplyStartTime: Nullable<number>
    changedChargeApplyStartTimeUnit: Nullable<UNIT_TIME>
    changedCharge: Nullable<number>
    changedChargeUnit: Nullable<CHARGE_UNIT>
    changedChargeUnitTime: Nullable<number>
}

export default class ReserveFacilityPaymentModel {
    private paymentObservers: ReserveFacilityModelPaymentObserver[] = []

    private paymentFactory: ReservePaymentCalculatorFactory

    private startDateMoment: Nullable<moment.Moment> = null
    private endDateMoment: Nullable<moment.Moment> = null
    private holidays: Nullable<string[]> = null
    private isStraddle: Nullable<boolean> = null
    private businessSchedules: Nullable<BusinessSchedule[]> = null

    constructor(paymentFactory: ReservePaymentCalculatorFactory) {
        this.paymentFactory = paymentFactory
    }

    reset(): ReserveFacilityPaymentModel {
        this.paymentFactory = this.paymentFactory.reset()
        this.startDateMoment = null
        this.endDateMoment = null
        this.holidays = null
        this.isStraddle = null
        this.businessSchedules = null
        this.paymentObservers = []

        return this
    }

    addPaymentInformationObserver(
        observer: ReserveFacilityModelPaymentObserver,
    ): ReserveFacilityPaymentModel {
        this.paymentObservers = [...this.paymentObservers, observer]

        this.calculatePaymentAndNotify()
        return this
    }

    withStayAvailable(stayAvailability: boolean): ReserveFacilityPaymentModel {
        this.paymentFactory.withStayAvailable(stayAvailability)

        this.calculatePaymentAndNotify()
        return this
    }

    withOvernightFacilityData(
        overnightFacilityData: Nullable<OvernightFacilitySetting>,
    ): ReserveFacilityPaymentModel {
        this.paymentFactory.withOvernightFacilityData(overnightFacilityData)

        this.calculatePaymentAndNotify()
        return this
    }

    withDurationFacilityData(
        durationFacilityData: Nullable<DurationFacilitySetting>,
    ): ReserveFacilityPaymentModel {
        this.businessSchedules = (durationFacilityData === null)
            ? null
            : durationFacilityData.businessSchedules

        this.isStraddle = (this.businessSchedules === null)
            ? null
            : this.businessSchedules
                .some(schedule => schedule.businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE)

        if (this.isStraddle) {
            this.paymentFactory.withBusinessScheduleForStraddle(this.businessSchedules)
        } else {
            this.businessSchedules =
                this.updateBusinessSchedulesSplitAllDayToEachDayOfWeeks(this.businessSchedules)

            const businessSchedulesForNormal = this.updateBusinessSchedulesDependOnTargetDate(
                this.startDateMoment,
                this.endDateMoment,
                this.businessSchedules,
            )
            this.paymentFactory.withBusinessSchedulesDependOnTargetDate(businessSchedulesForNormal)
        }

        this.calculatePaymentAndNotify()
        return this
    }

    withHolidays(holidays: Nullable<string[]>): ReserveFacilityPaymentModel {
        this.holidays = holidays

        this.calculatePaymentAndNotify()
        return this
    }

    withFacilityOptionSettings(facilityOptionSettings: OptionSetting[]): ReserveFacilityPaymentModel {
        this.paymentFactory.withFacilityOptionSettings(facilityOptionSettings)

        this.calculatePaymentAndNotify()
        return this
    }

    withReservationOptions(options: ReservationOption[]): ReserveFacilityPaymentModel {
        this.paymentFactory.withReservationOptions(options)

        this.calculatePaymentAndNotify()
        return this
    }

    withOvernightReservationData(startDate: Nullable<string>, stayCount: number): ReserveFacilityPaymentModel {
        this.paymentFactory.withOvernightReservationData(startDate, stayCount)

        this.calculatePaymentAndNotify()
        return this
    }

    withDurationReservationData(
        startDate: string,
        startTime: Nullable<string>,
        endDate: Nullable<string>,
        endTime: Nullable<string>,
    ): ReserveFacilityPaymentModel {
        this.startDateMoment = startTime ? convertToNullableMoment(startDate, startTime) : null
        this.endDateMoment = endTime ? convertToNullableMoment(endDate, endTime) : null

        if (!this.isStraddle) {
            const businessSchedulesForNormal = this.updateBusinessSchedulesDependOnTargetDate(
                this.startDateMoment,
                this.endDateMoment,
                this.businessSchedules,
            )
            this.paymentFactory.withBusinessSchedulesDependOnTargetDate(businessSchedulesForNormal)
        }
        this.paymentFactory.withDurationReservationData(startDate, startTime, endDate, endTime)

        this.calculatePaymentAndNotify()
        return this
    }

    private updateBusinessSchedulesSplitAllDayToEachDayOfWeeks(
        businessSchedules: Nullable<BusinessSchedule[]>,
    ): Nullable<BusinessSchedule[]> {
        if (businessSchedules === null) {
            return null
        }

        const needToSplit = businessSchedules
            .some(schedule => schedule.businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS)
        if (!needToSplit) {
            return businessSchedules
        }
        return PaymentModelSchedulePreProcessor.splitAllDaysBusinessSchedulesByDayOfWeeks(businessSchedules)
    }

    private updateBusinessSchedulesDependOnTargetDate(
        startDateMoment: Nullable<moment.Moment>,
        endDateMoment: Nullable<moment.Moment>,
        businessSchedules: Nullable<BusinessSchedule[]>,
    ): Nullable<BusinessScheduleWithDateTime[]> {
        if (startDateMoment === null || endDateMoment === null) {
            return null
        }
        if (businessSchedules === null) {
            return null
        }

        return PaymentModelSchedulePreProcessor.updateBusinessSchedulesDependOnTargetDate(
            businessSchedules,
            this.holidays,
            startDateMoment,
            endDateMoment,
        )
    }

    private calculatePaymentAndNotify(): void {
        const calculator: Nullable<ReservePaymentCalculator> = this.paymentFactory.create()
        const defaultPaymentInformation: PaymentInformation = {items: [], totalCharge: null}
        this.paymentObservers.forEach(
            observer => observer.onChangePayment(
                ((calculator !== null && calculator.isEnableCalculate()))
                    ? calculator.calculate()
                    : defaultPaymentInformation,
            ),
        )
    }
}
